import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { startOfWeek, format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import { createCustodySchedule, subscribeToCustodySchedules } from '@coparent/shared/src/services/custodyService';
import { CUSTODY_PATTERNS } from '@coparent/shared/src/data/defaultPatterns';
import { DAYS_OF_WEEK_SHORT } from '@coparent/shared/src/utils/constants';
import { HiCheck } from 'react-icons/hi';
import toast from 'react-hot-toast';
import NotificationBell from '../notifications/NotificationBell';

const CustodySetup = () => {
  const { currentUser } = useAuth();
  const { familyId, family, isHouseholdMember } = useFamily();
  const navigate = useNavigate();
  const [selectedPattern, setSelectedPattern] = useState('2-2-3');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);

  // 2-2-3 configuration: which parent gets Mon-Tue and which gets the first weekend
  const [monTueParentIdx, setMonTueParentIdx] = useState(0);
  const [firstWeekendParentIdx, setFirstWeekendParentIdx] = useState(0);

  // Custom 14-day builder: user-authored parent-per-day array (1 or 2)
  const [customCycleDays, setCustomCycleDays] = useState(
    () => [...(CUSTODY_PATTERNS.find(p => p.id === 'custom')?.cycleDays || [])]
  );

  // Pre-select the currently-active schedule so reopening this page reflects what's
  // actually saved (instead of always defaulting to 2-2-3). Hydrate once, from the
  // newest active schedule, and never clobber in-progress edits.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!familyId) return undefined;
    const unsub = subscribeToCustodySchedules(familyId, (schedules) => {
      if (hydratedRef.current || !schedules.length) return;
      hydratedRef.current = true;
      const active = schedules[0];
      const known = CUSTODY_PATTERNS.find(p => p.id === active.pattern);
      const pid = known ? active.pattern : 'custom';
      setSelectedPattern(pid);
      if (pid === 'custom' && Array.isArray(active.cycleDays) && active.cycleDays.length) {
        setCustomCycleDays([...active.cycleDays]);
      }
      if (pid === '2-2-3' && Array.isArray(active.cycleDays) && active.cycleDays.length >= 5) {
        setMonTueParentIdx((active.cycleDays[0] || 1) - 1);
        setFirstWeekendParentIdx((active.cycleDays[4] || 1) - 1);
      }
      if (active.startDate) {
        const d = active.startDate.toDate ? active.startDate.toDate() : new Date(active.startDate);
        if (!isNaN(d.getTime())) setStartDate(format(d, 'yyyy-MM-dd'));
      }
    });
    return unsub;
  }, [familyId]);

  const pattern = CUSTODY_PATTERNS.find(p => p.id === selectedPattern);

  const toggleCustomDay = (dayIndex) => {
    setCustomCycleDays(prev => {
      const next = [...prev];
      next[dayIndex] = next[dayIndex] === 1 ? 2 : 1;
      return next;
    });
  };

  const handlePatternSelect = (id) => {
    setSelectedPattern(id);
    setMonTueParentIdx(0);
    setFirstWeekendParentIdx(0);
  };

  // Build custom cycleDays for 2-2-3 based on user configuration
  const build223CycleDays = () => {
    const mt = monTueParentIdx + 1;
    const wt = mt === 1 ? 2 : 1;
    const fw = firstWeekendParentIdx + 1;
    const sw = fw === 1 ? 2 : 1;
    return [
      mt, mt, wt, wt, fw, fw, fw,
      mt, mt, wt, wt, sw, sw, sw
    ];
  };

  const getEffectiveCycleDays = () => {
    if (selectedPattern === 'custom') return customCycleDays;
    if (selectedPattern === '2-2-3') return build223CycleDays();
    return pattern?.cycleDays || [];
  };

  // Snap start date: 2-2-3 to Monday, custom to Sunday (so cycleDays[0] is a Sunday)
  const getEffectiveStartDate = () => {
    if (!startDate) return startDate;
    const parsed = new Date(startDate + 'T00:00:00');
    if (isNaN(parsed.getTime())) return startDate;
    if (selectedPattern === '2-2-3') {
      const monday = startOfWeek(parsed, { weekStartsOn: 1 });
      return format(monday, 'yyyy-MM-dd');
    }
    if (selectedPattern === 'custom') {
      const sunday = startOfWeek(parsed, { weekStartsOn: 0 });
      return format(sunday, 'yyyy-MM-dd');
    }
    return startDate;
  };

  const handleSave = async () => {
    if (!familyId) return;
    setLoading(true);
    try {
      const effectiveStart = getEffectiveStartDate();
      await createCustodySchedule(familyId, {
        type: 'recurring',
        pattern: selectedPattern,
        startDate: new Date(effectiveStart + 'T00:00:00'),
        endDate: null,
        cycleLength: pattern.cycleLength,
        cycleDays: getEffectiveCycleDays(),
        createdBy: currentUser.uid
      });
      toast.success('Custody schedule saved!');
      navigate('/calendar');
    } catch (error) {
      toast.error('Failed to save schedule');
    }
    setLoading(false);
  };

  const renderPatternPreview = () => {
    // Custom uses its own interactive builder below as the live preview
    if (!pattern || !family || selectedPattern === 'custom') return null;
    const parent1 = family.parentProfiles[family.parentIds[0]];
    const parent2 = family.parentProfiles[family.parentIds[1]] || { displayName: 'Co-Parent', color: '#ec4899' };
    const effectiveCycleDays = getEffectiveCycleDays();

    return (
      <div className="mt-5 card bg-surface-50 border-surface-200 p-5">
        <h4 className="text-xs font-semibold text-ink-500 mb-3 uppercase tracking-wide">Preview ({pattern.cycleLength / 7}-week cycle)</h4>
        {[0, 1].map(week => (
          <div key={week} className="mb-3 last:mb-0">
            <p className="text-[0.625rem] text-ink-400 mb-1.5 font-bold uppercase tracking-wider">Week {week + 1}</p>
            <div className="flex gap-1">
              {effectiveCycleDays.slice(week * 7, (week + 1) * 7).map((parentIndex, i) => {
                const p = parentIndex === 1 ? parent1 : parent2;
                return (
                  <div
                    key={i}
                    className="flex-1 text-center py-2 rounded-lg text-[0.625rem] font-bold text-white"
                    style={{ backgroundColor: p?.color || (parentIndex === 1 ? '#3b82f6' : '#ec4899') }}
                  >
                    {DAYS_OF_WEEK_SHORT[(i + 1) % 7]}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const render223Config = () => {
    if (selectedPattern !== '2-2-3' || !family || family.parentIds.length < 2) return null;
    const parents = family.parentIds.map((pid, idx) => ({
      idx,
      name: family.parentProfiles[pid]?.displayName || `Parent ${idx + 1}`,
      color: family.parentProfiles[pid]?.color || (idx === 0 ? '#3b82f6' : '#ec4899'),
    }));

    return (
      <div className="mt-5 space-y-4">
        {/* Mon-Tue picker */}
        <div>
          <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">
            Who has Mon–Tue?
          </label>
          <div className="flex gap-2">
            {parents.map(p => (
              <button
                key={p.idx}
                onClick={() => setMonTueParentIdx(p.idx)}
                className={`flex-1 p-3 rounded-xl border-2 transition-all duration-200 text-center ${
                  monTueParentIdx === p.idx
                    ? 'border-ink-800 bg-surface-50 shadow-warm-sm'
                    : 'border-surface-200 hover:border-surface-300'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: p.color }}
                  />
                  <span className="text-sm font-semibold text-ink-900">{p.name}</span>
                </div>
                <p className="text-[0.625rem] text-ink-400 mt-1">
                  {monTueParentIdx === p.idx ? 'Mon–Tue' : 'Wed–Thu'}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* First weekend picker */}
        <div>
          <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">
            Who has the first weekend (Fri–Sun)?
          </label>
          <div className="flex gap-2">
            {parents.map(p => (
              <button
                key={p.idx}
                onClick={() => setFirstWeekendParentIdx(p.idx)}
                className={`flex-1 p-3 rounded-xl border-2 transition-all duration-200 text-center ${
                  firstWeekendParentIdx === p.idx
                    ? 'border-ink-800 bg-surface-50 shadow-warm-sm'
                    : 'border-surface-200 hover:border-surface-300'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: p.color }}
                  />
                  <span className="text-sm font-semibold text-ink-900">{p.name}</span>
                </div>
                <p className="text-[0.625rem] text-ink-400 mt-1">
                  {firstWeekendParentIdx === p.idx ? 'Week 1 weekend' : 'Week 2 weekend'}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderCustomBuilder = () => {
    if (selectedPattern !== 'custom' || !family) return null;
    const parent1 = family.parentProfiles[family.parentIds[0]];
    const parent2 = family.parentProfiles[family.parentIds[1]] || { displayName: 'Co-Parent', color: '#ec4899' };
    const colorFor = (idx) => (idx === 1 ? (parent1?.color || '#3b82f6') : (parent2?.color || '#ec4899'));
    const nameFor = (idx) => (idx === 1 ? (parent1?.displayName || 'Parent A') : (parent2?.displayName || 'Parent B'));
    const countA = customCycleDays.filter(v => v === 1).length;
    const countB = customCycleDays.length - countA;

    return (
      <div className="mt-5 card bg-surface-50 border-surface-200 p-5">
        <h4 className="text-xs font-semibold text-ink-500 mb-1 uppercase tracking-wide">Build your schedule</h4>
        <p className="text-xs text-ink-400 mb-3">Tap a day to switch who has the kids. It repeats every two weeks.</p>

        <div className="flex items-center gap-4 mb-3">
          {[1, 2].map(idx => (
            <div key={idx} className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: colorFor(idx) }} />
              <span className="text-xs font-semibold text-ink-700">{nameFor(idx)}</span>
            </div>
          ))}
          <span className="ml-auto text-xs text-ink-400">
            {countA === countB ? 'Even split' : `${nameFor(1)} ${countA} · ${nameFor(2)} ${countB}`}
          </span>
        </div>

        {[0, 1].map(week => (
          <div key={week} className="mb-3 last:mb-0">
            <p className="text-[0.625rem] text-ink-400 mb-1.5 font-bold uppercase tracking-wider">Week {week + 1}</p>
            <div className="flex gap-1">
              {Array.from({ length: 7 }, (_, i) => {
                const dayIndex = week * 7 + i;
                const idx = customCycleDays[dayIndex];
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleCustomDay(dayIndex)}
                    className="flex-1 flex flex-col items-center gap-0.5 py-2 rounded-lg border-2 transition-all duration-150"
                    style={{ backgroundColor: colorFor(idx) + '22', borderColor: colorFor(idx) }}
                  >
                    <span className="text-[0.5rem] font-bold uppercase text-ink-400">{DAYS_OF_WEEK_SHORT[i]}</span>
                    <span className="text-xs font-bold" style={{ color: colorFor(idx) }}>
                      {nameFor(idx).charAt(0).toUpperCase()}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (isHouseholdMember) {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in text-center py-20">
        <h2 className="text-xl font-bold text-ink-900 mb-2">View Only</h2>
        <p className="text-sm text-ink-400">Custody schedules can only be modified by the primary co-parents.</p>
      </div>
    );
  }

  const effectiveStart = getEffectiveStartDate();
  const startSnapped = (selectedPattern === '2-2-3' || selectedPattern === 'custom') && effectiveStart !== startDate;

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between page-header">
        <div>
          <h2 className="page-title">Custody Schedule</h2>
          <p className="page-subtitle">Choose a custody rotation pattern.</p>
        </div>
        <NotificationBell />
      </div>

      <div className="space-y-2.5">
        {CUSTODY_PATTERNS.map(p => (
          <button
            key={p.id}
            onClick={() => handlePatternSelect(p.id)}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
              selectedPattern === p.id
                ? 'border-ink-800 bg-surface-50 shadow-warm-sm'
                : 'border-surface-200 hover:border-surface-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-ink-900 text-sm">{p.name}</div>
                <div className="text-xs text-ink-400 mt-0.5">{p.description}</div>
              </div>
              {selectedPattern === p.id && (
                <div className="w-6 h-6 rounded-full bg-ink-800 flex items-center justify-center flex-shrink-0">
                  <HiCheck className="text-white text-xs" />
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {render223Config()}
      {renderCustomBuilder()}
      {renderPatternPreview()}

      <div className="mt-6">
        <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Start Date</label>
        <input
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          type="date"
          className="input-base"
        />
        {startSnapped && (
          <p className="text-xs text-ink-400 mt-1.5">
            Start date will be adjusted to {selectedPattern === 'custom' ? 'Sunday' : 'Monday'} ({effectiveStart}) for weekly alignment.
          </p>
        )}
      </div>

      <button
        onClick={handleSave}
        disabled={loading}
        className="btn-primary w-full mt-6"
      >
        {loading ? 'Saving...' : 'Save Schedule'}
      </button>
    </div>
  );
};

export default CustodySetup;
